import { create } from 'zustand';

interface ConfirmOptions {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDangerous?: boolean;
}

interface PromptOptions {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    defaultValue?: string;
}

interface ToastOptions {
    title?: string;
    message: string;
    type?: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
}

interface ToastState extends ToastOptions {
    id: string;
}

interface UIState {
    // Confirm Dialog State
    confirmState: ConfirmOptions | null;
    confirmResolve: ((value: boolean) => void) | null;
    showConfirm: (options: ConfirmOptions | string) => Promise<boolean>;
    resolveConfirm: (value: boolean) => void;

    // Prompt Dialog State
    promptState: PromptOptions | null;
    promptResolve: ((value: string | null) => void) | null;
    showPrompt: (options: PromptOptions | string) => Promise<string | null>;
    resolvePrompt: (value: string | null) => void;

    // Toast State
    toasts: ToastState[];
    showToast: (options: ToastOptions | string, type?: ToastOptions['type']) => void;
    removeToast: (id: string) => void;
}

export const useUIStore = create<UIState>((set, get) => ({
    // Confirm Dialog
    confirmState: null,
    confirmResolve: null,
    showConfirm: (options) => {
        return new Promise<boolean>((resolve) => {
            const parsedOptions: ConfirmOptions = typeof options === 'string'
                ? { message: options }
                : options;

            set({
                confirmState: parsedOptions,
                confirmResolve: resolve,
            });
        });
    },
    resolveConfirm: (value) => {
        const { confirmResolve } = get();
        if (confirmResolve) {
            confirmResolve(value);
        }
        set({ confirmState: null, confirmResolve: null });
    },

    // Prompt Dialog
    promptState: null,
    promptResolve: null,
    showPrompt: (options) => {
        return new Promise<string | null>((resolve) => {
            const parsedOptions: PromptOptions = typeof options === 'string'
                ? { message: options }
                : options;

            set({
                promptState: parsedOptions,
                promptResolve: resolve,
            });
        });
    },
    resolvePrompt: (value) => {
        const { promptResolve } = get();
        if (promptResolve) {
            promptResolve(value);
        }
        set({ promptState: null, promptResolve: null });
    },

    // Toasts
    toasts: [],
    showToast: (options, type) => {
        const parsedOptions: ToastOptions = typeof options === 'string'
            ? { message: options, type: type || 'info' }
            : options;

        const id = crypto.randomUUID();
        const toast: ToastState = { ...parsedOptions, id, type: parsedOptions.type || 'info' };

        set((state) => ({
            toasts: [...state.toasts, toast],
        }));

        setTimeout(() => {
            get().removeToast(id);
        }, parsedOptions.duration || 4000);
    },
    removeToast: (id) => {
        set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
        }));
    },
}));
